import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Eye, Calendar, User, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { format } from 'date-fns'
import type { Artist, ArtistOnboarding } from '@/types/models'
import { InvitingArtistAttribution } from '@/components/admin/InvitingArtistPickerDialog'
import { cn } from '@/lib/utils'

interface AvatarProps {
  src?: string
  alt: string
  displayName: string
}

function Avatar({ src, alt, displayName }: AvatarProps) {
  if (!src) {
    // Generate initials from display name
    const initials = displayName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
    
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
        {initials || <User className="w-6 h-6" />}
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-12 h-12 rounded-full object-cover"
      onError={(e) => {
        const target = e.target as HTMLImageElement
        // Replace with initials fallback
        const parent = target.parentElement
        if (parent) {
          const initials = displayName
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2)
          
          parent.innerHTML = `
            <div class="w-12 h-12 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white font-semibold">
              ${initials || '<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>'}
            </div>
          `
        }
      }}
    />
  )
}

interface OnboardingListViewProps {
  onboardings: ArtistOnboarding[]
  loading: boolean
  onViewDetails: (onboarding: ArtistOnboarding) => void
  onDelete?: (onboarding: ArtistOnboarding) => Promise<void>
  /** Full artist list (e.g. ArtistsDataContext.allArtists) for resolving `invitingArtist` */
  artistsForLookup?: Artist[]
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'scraper_started':
      return 'bg-orange-100 text-orange-800'
    case 'scraper_done':
      return 'bg-yellow-100 text-yellow-800'
    case 'ready_for_artist':
      return 'bg-blue-100 text-blue-800'
    case 'waiting_for_review':
      return 'bg-purple-100 text-purple-800'
    case 'done':
      return 'bg-green-100 text-green-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatStatus = (status: string) => {
  switch (status) {
    case 'scraper_started':
      return 'Scraper Started'
    case 'scraper_done':
      return 'Scraper Done'
    case 'ready_for_artist':
      return 'Ready for Artist'
    case 'waiting_for_review':
      return 'Waiting for Review'
    case 'done':
      return 'Completed'
    default:
      return status
  }
}

export function OnboardingListView({ 
  onboardings, 
  loading, 
  onViewDetails,
  onDelete,
  artistsForLookup = [],
}: OnboardingListViewProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleDelete = async (onboarding: ArtistOnboarding) => {
    if (!onDelete) return
    
    setDeletingId(onboarding.id)
    try {
      await onDelete(onboarding)
    } catch (error) {
      console.error('Error deleting onboarding:', error)
      // TODO: Show error toast
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} className="animate-pulse">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="h-6 bg-gray-200 rounded w-24"></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="space-y-1">
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                    <div className="h-3 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-20"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (onboardings.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <CardTitle className="mb-2">No Onboardings Found</CardTitle>
          <CardDescription>
            There are currently no artist onboardings to review.
          </CardDescription>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {onboardings.map((onboarding) => (
        <Card key={onboarding.id} className="hover:shadow-md transition-shadow">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-lg">
                    {onboarding.displayName.startsWith('@')
                      ? onboarding.displayName
                      : `@${onboarding.displayName}`}
                  </CardTitle>
                  <Badge
                    className={cn(getStatusColor(onboarding.status), 'shrink-0')}
                    variant="secondary"
                  >
                    {formatStatus(onboarding.status)}
                  </Badge>
                </div>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Calendar className="w-4 h-4 shrink-0" />
                  {onboarding.createdAt 
                    ? format(onboarding.createdAt, 'MMM dd, yyyy HH:mm')
                    : 'No date'
                  }
                </CardDescription>
              </div>
              <div className="shrink-0 sm:max-w-[14rem] sm:text-right border rounded-lg bg-muted/40 px-3 py-2 space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Invited by
                </p>
                <InvitingArtistAttribution
                  artists={artistsForLookup}
                  invitingArtistId={onboarding.invitingArtist}
                  alignRight
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Avatar
                  src={onboarding.profileImageUrl}
                  alt={onboarding.displayName}
                  displayName={onboarding.displayName}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {onboarding.bio || 'No bio available'}
                  </p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                    <span>{onboarding.postUrls.length} posts</span>
                    {onboarding.tattooImageUrls && (
                      <span>{onboarding.tattooImageUrls.length} tattoos selected</span>
                    )}
                    {onboarding.flashes && onboarding.flashes.length > 0 && (
                      <span>{onboarding.flashes.length} flashes</span>
                    )}
                    {onboarding.location && (
                      <span>📍 {onboarding.location}</span>
                    )}
                    {onboarding.code && (
                      <span className="font-mono bg-muted px-1 rounded">
                        Code: {onboarding.code}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => onViewDetails(onboarding)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View Details
                </Button>
                
                {onDelete && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-destructive hover:text-destructive"
                        disabled={deletingId === onboarding.id}
                      >
                        <Trash2 className="w-4 h-4" />
                        {deletingId === onboarding.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Archive Onboarding</DialogTitle>
                        <DialogDescription>
                          Are you sure you want to archive the onboarding for <strong>{onboarding.displayName}</strong>? 
                          This will move the onboarding to the archived collection and remove it from the active list.
                          This action cannot be undone from this interface.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline">Cancel</Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleDelete(onboarding)}
                          disabled={deletingId === onboarding.id}
                        >
                          {deletingId === onboarding.id ? 'Archiving...' : 'Archive Onboarding'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
